import { NavLink } from 'react-router-dom'
import { useState } from 'react'
import { 
  HomeIcon, 
  UserGroupIcon, 
  BeakerIcon, 
  ArchiveBoxIcon,
  CogIcon,
  ChartBarIcon,
  HeartIcon,
  TruckIcon,
  ClipboardDocumentCheckIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'

const Sidebar = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['animal-management'])

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionKey) 
        ? prev.filter(key => key !== sectionKey)
        : [...prev, sectionKey]
    )
  }

  const navigationSections = [
    {
      title: 'Dashboard',
      key: 'dashboard',
      items: [
        { name: 'Main Dashboard', href: '/', icon: HomeIcon },
        { name: 'Analytics Dashboard', href: '/modules/analytics-dashboard', icon: ChartBarIcon },
        { name: 'Realtime Monitoring', href: '/modules/realtime-monitoring', icon: ChartBarIcon },
      ]
    },
    {
      title: 'Animal Management',
      key: 'animal-management',
      items: [
        { name: 'Animals', href: '/modules/animals', icon: UserGroupIcon },
        { name: 'Animal Assignments', href: '/modules/animal-assignments', icon: UserGroupIcon },
        { name: 'Breeding', href: '/modules/breeding', icon: HeartIcon },
        { name: 'Reproduction', href: '/modules/reproduction', icon: HeartIcon },
        { name: 'Embryo Transfer', href: '/modules/embryo-transfer', icon: HeartIcon },
        { name: 'Embryo Detail', href: '/modules/embryo-detail', icon: HeartIcon },
        { name: 'Phenotype', href: '/modules/phenotype', icon: UserGroupIcon },
        { name: 'Fertilization', href: '/modules/fertilization', icon: HeartIcon },
        { name: 'Flushing', href: '/modules/flushing', icon: HeartIcon },
        { name: 'OPU', href: '/modules/opu', icon: BeakerIcon },
      ]
    },
    {
      title: 'Veterinary Services',
      key: 'veterinary',
      items: [
        { name: 'Vaccinations', href: '/modules/vaccinations', icon: BeakerIcon },
        { name: 'Ultrasound', href: '/modules/ultrasound', icon: BeakerIcon },
        { name: 'Internal Medicine', href: '/modules/internal-medicine', icon: BeakerIcon },
        { name: 'Clinical Hub', href: '/modules/clinical-hub', icon: BeakerIcon },
      ]
    },
    {
      title: 'Laboratory & Genomics',
      key: 'laboratory',
      items: [
        { name: 'Laboratory', href: '/modules/laboratory', icon: BeakerIcon },
        { name: 'Media Preparation', href: '/modules/media-preparation', icon: BeakerIcon },
        { name: 'Genomics', href: '/modules/genomics', icon: BeakerIcon },
        { name: 'Genomic Intelligence', href: '/modules/genomic-intelligence', icon: BeakerIcon },
        { name: 'Biobank', href: '/modules/biobank', icon: ArchiveBoxIcon },
        { name: 'Sample Management', href: '/modules/sample-management', icon: ArchiveBoxIcon },
        { name: 'Lab Results', href: '/modules/lab-results', icon: BeakerIcon },
        { name: 'SNP Analysis', href: '/modules/snp-analysis', icon: BeakerIcon },
        { name: 'Semen Management', href: '/modules/semen-management', icon: ArchiveBoxIcon },
        { name: 'BeadChip Mappings', href: '/modules/beadchip-mappings', icon: BeakerIcon },
      ]
    },
    {
      title: 'Farm Operations',
      key: 'farm-operations',
      items: [
        { name: 'Farm Management', href: '/modules/farm-management', icon: BuildingOfficeIcon },
        { name: 'Farm Analytics', href: '/modules/farm-analytics', icon: ChartBarIcon },
        { name: 'Farm Compliance', href: '/modules/farm-compliance', icon: ClipboardDocumentCheckIcon },
        { name: 'Dropleting Management', href: '/modules/milking-management', icon: BuildingOfficeIcon },
        { name: 'Fattening Management', href: '/modules/fattening-management', icon: BuildingOfficeIcon },
        { name: 'Inventory', href: '/modules/inventory', icon: ArchiveBoxIcon },
        { name: 'Inventory Analytics', href: '/modules/inventory-analytics', icon: ChartBarIcon },
        { name: 'Equipment', href: '/modules/equipment', icon: WrenchScrewdriverIcon },
        { name: 'Procurement Management', href: '/modules/procurement-management', icon: TruckIcon },
      ]
    },
    {
      title: 'Business Management',
      key: 'business',
      items: [
        { name: 'Finance', href: '/modules/finance', icon: CurrencyDollarIcon },
        { name: 'Customers', href: '/modules/customers', icon: UserGroupIcon },
        { name: 'Tenders', href: '/modules/tenders', icon: CurrencyDollarIcon },
        { name: 'Human Resources', href: '/modules/human-resources', icon: UserGroupIcon },
      ]
    },
    {
      title: 'AI & Analytics',
      key: 'ai-analytics',
      items: [
        { name: 'AI Analytics', href: '/modules/ai-analytics', icon: ChartBarIcon },
        { name: 'Research', href: '/modules/research', icon: MagnifyingGlassIcon },
      ]
    },
    {
      title: 'System Management',
      key: 'system',
      items: [
        { name: 'Calendar', href: '/modules/calendar', icon: CalendarIcon },
        { name: 'Data Integration', href: '/modules/data-integration', icon: CogIcon },
        { name: 'Integration Hub', href: '/modules/integration-hub', icon: CogIcon },
        { name: 'Admin', href: '/modules/admin', icon: CogIcon },
        { name: 'Settings', href: '/modules/settings', icon: CogIcon },
      ]
    }
  ]

  return (
    <div className="hidden md:flex md:flex-shrink-0">
      <div className="flex flex-col w-80">
        <div className="flex flex-col h-0 flex-1 bg-gray-800">
          <div className="flex items-center h-16 flex-shrink-0 px-4 bg-gray-900">
            <div className="flex items-center">
              <HeartIcon className="h-8 w-8 text-blue-400 mr-2" />
              <h1 className="text-xl font-semibold text-white">Reprotech</h1>
            </div>
            <div className="ml-auto text-xs text-gray-400">v2.0</div>
          </div>
          <div className="flex-1 flex flex-col overflow-y-auto">
            <nav className="flex-1 px-2 py-4 bg-gray-800 space-y-2">
              {navigationSections.map((section) => (
                <div key={section.key}>
                  <button
                    onClick={() => toggleSection(section.key)}
                    className="w-full flex items-center justify-between px-2 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white rounded-md transition-colors"
                  >
                    <span className="flex items-center">
                      <span className="text-xs font-semibold tracking-wider uppercase">{section.title}</span>
                    </span>
                    {expandedSections.includes(section.key) ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                  
                  {expandedSections.includes(section.key) && (
                    <div className="ml-4 mt-1 space-y-1">
                      {section.items.map((item) => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={({ isActive }) =>
                            `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              isActive
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`
                          }
                        >
                          <item.icon className="mr-3 flex-shrink-0 h-5 w-5" />
                          <span className="truncate">{item.name}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
          
          {/* Footer */}
          <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
            <div className="flex-shrink-0 w-full group block">
              <div className="flex items-center">
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-400 group-hover:text-gray-300">
                    Veterinary Biotechnology Platform
                  </p>
                  <p className="text-xs font-medium text-gray-500 group-hover:text-gray-400">
                    45+ Specialized Modules
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Sidebar 
